import { Lightning } from "@lightningjs/sdk";
import posthog from "posthog-js";
import { ChannelList } from "./components/ChannelList/ChannelList";
import { LiveVideoPlayer } from "./components/LiveVideoPlayer/LiveVideoPlayer";
import { ChannelItem } from "./components/ChannelItem/ChannelItem";
import { ChannelItemModel, ChannelModel } from "../../lib/models";
import theme from "../../lib/theme";
import {MENU_WIDTH, PAGES} from "../../lib/utils";
import { recordVideoWatchtime } from "../../api/mutations";

interface LiveTvGroup {
    name: string;
    channels: ChannelModel[];
}

interface LiveChannelsTemplateSpec extends Lightning.Component.TemplateSpec {
    List: typeof ChannelList;
    VideoPlayer: typeof LiveVideoPlayer;
    ChannelOverlay: typeof ChannelItem;
}

interface LiveChannelsTypeConfig extends Lightning.Component.TypeConfig {
    IsPage: true;
}

export class LiveChannels
    extends Lightning.Component<LiveChannelsTemplateSpec, LiveChannelsTypeConfig>
    implements Lightning.Component.ImplementTemplateSpec<LiveChannelsTemplateSpec>
{
    _showingList = true;
    _overlayTimeout: number | null = null;

    private _currentChannelStartTime: number | null = null;
    private _currentChannel: ChannelItemModel | null = null;

    readonly List = this.getByRef("List")!;
    readonly VideoPlayer = this.getByRef("VideoPlayer")!;
    readonly ChannelOverlay = this.getByRef("ChannelOverlay")!;

    static override _template(): Lightning.Component.Template<LiveChannelsTemplateSpec> {
        return {
            w: theme.layout.w,
            h: theme.layout.h,
            VideoPlayer: {
                type: LiveVideoPlayer
            },
            List: {
                type: ChannelList,
                x: MENU_WIDTH + 28,
                y: 40,
                w: theme.layout.w - MENU_WIDTH - 28,
                h: theme.layout.h - 40
            },
            ChannelOverlay: {
                type: ChannelItem,
                alpha: 0,
                x: theme.channelOverlay.content.x,
                y: theme.layout.h - theme.layout.gap.y - theme.channelOverlay.h
            }
        };
    }

    override _getFocused(): Lightning.Component | null | undefined {
        return this._showingList ? this.List : this.VideoPlayer;
    }

    override _firstActive() {
        this.application.emit("setActivePage", PAGES.live.name);
    }

    override _active() {
        if (window.selectedChannel) {
            const foundChannelItem = this.List.items.find(
                (item: any) => !item.isGroupTitle && item.item?.name === window.selectedChannel?.name
            );

            if (foundChannelItem) {
                this._playChannel(foundChannelItem.item);
                this.List.index = this.List.items.findIndex(
                    (item: any) => !item.isGroupTitle && item.item?.name === window.selectedChannel?.name
                );
            } else {
                const firstChannel = this._getFirstChannelItem();
                if (firstChannel) {
                    this._playChannel(firstChannel.item);
                }
            }
        } else if (this.List.items.length > 0) {
            const firstChannel = this._getFirstChannelItem();
            if (firstChannel) {
                this._playChannel(firstChannel.item);
            }
        }
    }

    override _inactive() {
        this._trackChannelWatchTime();
        this._currentChannel = null;
        this._currentChannelStartTime = null;
        this.stage.gc();
    }

    override _handleLeft() {
        return this._handleNavigation(-1);
    }

    override _handleRight() {
        return this._handleNavigation(1);
    }

    override _handleUp() {
        return this._toggleList();
    }

    override _handleDown() {
        return this._toggleList();
    }

    override _handleEnter() {
        if (this._currentChannel) {
            this._showOverlay(this._currentChannel);
        }
    }

    override _handleBack() {
        if (!this._showingList) {
            this._showList();
            return true;
        }

        return false;
        // if (!Router.getHistory().length) return false;
        // Router.go(Router.getHistory().length * -1);
    }

    $liveChannelSelected(channel: ChannelItemModel) {
        this.application.emit("hideMenu");
        this._hideList();
        this._playChannel(channel);
    }

    _showOverlay(channel: ChannelItemModel) {
        this.ChannelOverlay.item = channel;
        this.ChannelOverlay.patch({ smooth: { alpha: 1 } });

        if (this._overlayTimeout) {
            clearTimeout(this._overlayTimeout);
        }

        this._overlayTimeout = setTimeout(() => {
            this.ChannelOverlay.patch({ smooth: { alpha: 0 } });
        }, 5000) as unknown as number;
    }

    _toggleList(): boolean {
        if (!this._showingList) {
            this._showList();
        }
        return this._showingList;
    }

    _handleNavigation(direction: number): boolean {
        if (this._showingList) return false;

        const items = this.List.items;
        // Step in the travel direction, skipping group-title rows *in that same
        // direction* so left/right cross group boundaries both ways. (The List's
        // own index setter always skips forward, which trapped you at a group's
        // left edge.)
        let nextIndex = this.List.index + direction;
        while (nextIndex >= 0 && nextIndex < items.length && items[nextIndex]?.isGroupTitle) {
            nextIndex += direction;
        }

        // Past the end → wrap to the other end, then skip any group title inward.
        if (nextIndex < 0 || nextIndex >= items.length) {
            nextIndex = direction > 0 ? 0 : items.length - 1;
            while (nextIndex >= 0 && nextIndex < items.length && items[nextIndex]?.isGroupTitle) {
                nextIndex += direction;
            }
            // No actual channels in the list — nothing to do.
            if (nextIndex < 0 || nextIndex >= items.length) return true;
        }

        const currentItem = items[nextIndex];
        if (currentItem && !currentItem.isGroupTitle && currentItem.item) {
            this.List.index = nextIndex; // already resolved to a channel; setter is a no-op skip
            this._playChannel(currentItem.item);
            this._showOverlay(currentItem.item);
        }
        return true;
    }

    _trackChannelWatchTime() {
        if (this._currentChannel && this._currentChannelStartTime) {
            const watchTimeInSeconds = Math.floor((Date.now() - this._currentChannelStartTime) / 1000);

            posthog.capture("ChannelWatchTime", {
                channelId: this._currentChannel.id,
                channelName: this._currentChannel.name,
                watchTimeSeconds: watchTimeInSeconds
            });

            // Send channel watchtime to CMS
            const contentId = Number(this._currentChannel.id);
            if (contentId) {
                recordVideoWatchtime(contentId, watchTimeInSeconds);
            }
        }
    }

    _getFirstChannelItem(): any | null {
        return this.List.items.find((item: any) => !item.isGroupTitle && item.item) || null;
    }

    _playChannel(channel: ChannelItemModel) {
        if (!this.VideoPlayer.isChannelPlaying(channel)) {
            this._trackChannelWatchTime();
            this._currentChannel = channel;
            this._currentChannelStartTime = Date.now();
            this.VideoPlayer.channel = channel;
        }
    }

    _showList() {
        this.ChannelOverlay.patch({ smooth: { alpha: 0 } });

        this.application.emit("showMenu");
        this._showingList = true;
        this.List.patch({ smooth: { alpha: 1 } });
        this._refocus();
    }

    _hideList() {
        this._showingList = false;
        this.List.patch({ smooth: { alpha: 0 } });
    }

    set data(initialChannels: ChannelModel[]) {
        console.log("Initial channels received:", initialChannels.length);
        this.List.addItems(initialChannels);
    }

    set groups(liveTvGroups: LiveTvGroup[]) {
        console.log("Groups received:", liveTvGroups.length);
        this.List.addGroups(liveTvGroups);
    }
}
